import { ComponentType } from "react";
import { Helmet } from "react-helmet-async";

import { useFeatureFlags } from "@smart/bridge-feature-flags-dom";
import {
  AutoFillStatus,
  BannersVisibilityProvider,
  IntakeForm,
  LoadResponses,
  useAnalytics,
  Toast,
  FieldItem,
  VoiceConversation,
} from "@smart/bridge-intake-components-dom";
import { track } from "@smart/bridge-metrics-dom";
import { StaffDetails } from "@smart/bridge-types-basic/src/appointment";
import { useAsync } from "@smart/itops-hooks-dom";
import { Modal } from "@smart/itops-sb-design-system-dom";
import { extractId } from "@smart/itops-types-basic";
import { delay, inIframe } from "@smart/itops-utils-basic";

import { getContactSearchFieldOverride } from "./contact-search-field";
import {
  intakeApi,
  IntakeApiAIUserFeedback,
  IntakeApiBranding,
  IntakeApiForm,
  IntakeApiMatter,
  IntakeApiSubmission,
  IntakeApiTeam,
  IntakeApiVoiceGuide,
  useAddressLookup,
  useFileLookup,
  intakeWebsocket,
  useSubmissionAnalytics,
} from "../../hooks";

const AI_FILL_POLLING_INTERVAL = 2000;

const VoiceConversationWrapper =
  (onUseVoice: () => void): ComponentType<{}> =>
  () => <VoiceConversation onUseVoice={onUseVoice} />;

export type SubmissionFormProps = {
  matter?: IntakeApiMatter;
  team: IntakeApiTeam;
  form: IntakeApiForm;
  submission: IntakeApiSubmission;
  aiUserFeedback: IntakeApiAIUserFeedback | undefined;
  autoFillStatus?: AutoFillStatus;
  setAutoFillStatus: (status: AutoFillStatus | undefined) => void;
  hideTeam?: boolean;
  loadResponses: LoadResponses;
  staffDetails: StaffDetails[];
  reloadSubmission: () => void;
  reloadForSigningUrl: () => Promise<string | undefined>;
  featureFlagUserIdentified: boolean;
  captchaToken?: string;
  setToasts: (toasts: Toast[]) => void;
  setFields: (setFn: (fields: FieldItem[]) => FieldItem[]) => void;
  branding?: IntakeApiBranding;
  voiceGuide?: IntakeApiVoiceGuide;
  selectedLanguage?: string;
  setSelectedLanguage?: (language: string) => void;
  hasPendingDigitalSignature?: boolean;
  signingUrl?: string;
};

export const SubmissionForm = ({
  matter,
  team,
  form,
  submission,
  aiUserFeedback,
  autoFillStatus,
  setAutoFillStatus,
  hideTeam,
  loadResponses,
  staffDetails,
  reloadSubmission,
  reloadForSigningUrl,
  featureFlagUserIdentified,
  captchaToken,
  setToasts,
  setFields,
  branding,
  voiceGuide,
  selectedLanguage,
  setSelectedLanguage,
  hasPendingDigitalSignature,
  signingUrl,
}: SubmissionFormProps) => {
  const uris = {
    teamUri: team.uri,
    submissionUri: submission.uri,
    formUri: form.uri,
  };
  const lookup = {
    addressLookup: useAddressLookup(),
    fileLookup: useFileLookup(uris),
  };
  const { post, result, ...connection } = intakeApi.submit();
  const { sendEvent } = useAnalytics();
  const { leadPro, matterFlow } = useFeatureFlags();
  const { postTrackEvent } = useSubmissionAnalytics(extractId(submission.uri));

  useAsync(async () => {
    if (aiUserFeedback?.status === "failed") {
      setAutoFillStatus("failed");
      return;
    }
    if (!autoFillStatus && aiUserFeedback?.status === "generating") {
      setAutoFillStatus("filling");
      return;
    }
    if (autoFillStatus !== "filling" || aiUserFeedback?.status === "reviewed") {
      return;
    }

    if (aiUserFeedback?.status && aiUserFeedback.status !== "generating") {
      setAutoFillStatus("completed");
    } else {
      await delay(AI_FILL_POLLING_INTERVAL);
      reloadSubmission();
    }
  }, [autoFillStatus, aiUserFeedback, submission]);

  return (
    <>
      <Helmet>
        <title>{[team.name, form.title].filter(Boolean).join(" | ")}</title>
      </Helmet>
      <BannersVisibilityProvider
        initial={{
          LANGUAGE: !!setSelectedLanguage && !!form.supportedLanguages?.length,
          CONNECTION: !inIframe(),
          // "not yet dismissed", not "shown", banner has its own render guard
          // flag only ever turns off via close button
          AUTOFILL_FAILED: true,
        }}
      >
        <IntakeForm
          connection={connection}
          isMatterClosed={matter?.isClosed}
          form={{
            uri: form.uri,
            team,
            hideTeam,
            response: form.response,
            category: form.category,
            aiFillSettings: form.aiFillSettings,
            title: form.title,
            translations: form.translations,
            supportedLanguages: form.supportedLanguages,
            matterTypes: form.matterTypes,
            redirectUrl: form.redirectUrl,
          }}
          sections={form.sections}
          groups={form.groups}
          fields={form.fields}
          lookup={lookup}
          submission={submission}
          setToasts={setToasts}
          setFields={setFields}
          fieldComponentOverride={getContactSearchFieldOverride()}
          submitResponses={async (input) => {
            if (input.status === "completed") {
              track("Submission Completed", {
                formUri: uris.formUri,
                submissionUri: uris.submissionUri,
              });
              sendEvent({
                name: "form_submission",
                params: {
                  form_title: form.title,
                },
              });
            }

            const submitResult = await post({
              body: { ...uris, ...input, captchaToken },
            });

            if (submitResult?.digitalSignature?.signingUrl) {
              postTrackEvent({
                category: "track",
                name: "IntakeDigitalSignatureClientStarted",
                screen: "Submission",
                entryPoint: "IntakeSubmission",
                component: "Digital Signature Component",
                jiraKey: "LINK-3676",
                interactionType: "Active",
                properties: {
                  signatureType: "digital",
                  firmName: team.name,
                },
              });
            }

            return submitResult;
          }}
          reloadForSigningUrl={reloadForSigningUrl}
          loadResponses={loadResponses}
          voiceWebSocket={intakeWebsocket}
          VoiceConversationComponent={
            voiceGuide?.enabled
              ? VoiceConversationWrapper(() =>
                  postTrackEvent({
                    category: "track",
                    name: "VoiceFillUsed",
                    screen: "Submission",
                    entryPoint: "IntakeSubmission",
                    component: "VoiceFill",
                    jiraKey: "LINK-3384",
                    interactionType: "Active",
                    properties: { Name: form.title ?? "" },
                  }),
                )
              : undefined
          }
          shouldLoadDBResponses
          postSubmissionStatus={result?.submissionStatus}
          staffDetails={staffDetails}
          autoFillStatus={autoFillStatus}
          branding={branding}
          runAutoFilling={async (uploadedFileKeys: string[]) => {
            await post({
              body: {
                ...uris,
                responses: {},
                aiFillFiles: uploadedFileKeys,
                captchaToken,
              },
            });
            setAutoFillStatus("filling");
          }}
          resetAutoFilling={() => setAutoFillStatus(undefined)}
          submitAutofillFeedback={async (feedback) => {
            await post({
              body: {
                ...uris,
                responses: {},
                autofillFeedback: feedback,
                captchaToken,
              },
            });
          }}
          selectedLanguage={selectedLanguage}
          setSelectedLanguage={setSelectedLanguage}
          hasPendingDigitalSignature={hasPendingDigitalSignature}
          signingUrl={signingUrl}
          onSignatureModalClose={() =>
            postTrackEvent({
              category: "track",
              name: "IntakeDigitalSignatureAbandoned",
              screen: "Submission",
              entryPoint: "IntakeSubmission",
              component: "Digital Signature Component",
              jiraKey: "LINK-3678",
              interactionType: "Active",
              properties: {
                signatureType: "digital",
                firmName: team.name,
              },
            })
          }
        />
      </BannersVisibilityProvider>
      <Modal
        open={
          featureFlagUserIdentified &&
          ((leadPro === false && form.category === "lead") ||
            (matterFlow === false && form.category === "matter"))
        }
        header={{
          icon: "solidCircleInfo",
          text: "Form not available",
        }}
        footer={{
          buttons: [
            {
              variant: "primaryDefault",
              text: "OK",
            },
          ],
        }}
      >
        <p>The form is no longer available. Please contact {team.name}.</p>
      </Modal>
    </>
  );
};
